import { apm } from '@elastic/apm-rum'
import * as Sentry from '@sentry/react'
import { PayloadAction } from "@reduxjs/toolkit"
import queryString from "query-string"
import { all, call, delay, fork, put, race, retry, select, spawn, take, takeEvery, takeLeading } from "redux-saga/effects"
import { appConfig, appInit } from '../config'
import i18n from '../i18n/i18n'
import { cleanupIBState, setApplication as setIBApplication } from 'ib/redux/state'
import Api, { Authorize } from "../utils/api"
import { captureAdAttribution, AD_ATTRIBUTION_SESSION_GUARD } from "../utils/attribution"
import * as Enums from "../utils/enums"
import Routes from '../utils/routes'
import { captureStargateAttribution } from '../utils/stargateAttribution' // THPRT-6415
import { buildSigninEvent, buildUserLandedEvent, SubmittedCode, SubmittedStatus, USER_LANDED_SESSION_KEY } from '../utils/floodlightEvents'
import { venusApi } from '../components/Container/Dashboard/LoyaltyProgram/venus/api'
import * as Types from "../utils/types"
import { APIResponse, ApplicationStatus, AppSubmitedParams, KYCProvider, Transaction, UserApplication } from '../utils/types'
import Utils from "../utils/utils"
import { RESTRICTED_DOMAINS, MAX_ORIGIN_SRC_LENGTH } from './../utils/constants/DOMAINS'
import { isEuOriginSrc } from './../utils/euCountries'
import { __STORAGE } from './../utils/enums'
import { UserProfile } from './../utils/types'
import * as Actions from "./actions"
import { getAccounts, getOrganizationSettings } from './actions'
import * as ApiSlices from "./slices/api"
import {
    appError, bootCompleted, changeLanguage, cleanupAppState,
    debugMode, docTypesLoaded, initCountries, initError, setUserIP,
    organizationSettingsLoaded, setAccounts, setAllowedPlatforms, setAllowThinkInvest, setApplicationApproval,
    setApplicationCooldownCheck,
    setAppropriatenessLevel, setAppStatus, setAutoLogout,
    setIB, setIsDemoExpired, setIsSubscriptionTerminated, setKYCProvider,
    setLandingPage,
    setLastDeposit, setLoggedIn, setLoyaltyStatus, setMigration,
    setNewCreatedAccount, setNextTermsAndConditions,
    setPasswordResetToken, setPortalAccountDomain, setPreferredLanguages,
    setQuestions,
    setRedirectedFromWTR, setShowFA2Warning, setSubscriptionStatus, setTwoFa,
    setOriginSrc, setUserAppInfo, setUserCountry, setUserInsidePortal, setUserReferrerStatus, userAppsLoaded, userEmailPreverified, userEmailVerified, userLoaded
} from './slices/app'
import { setProgressStep, setProgressSteps, setShowProgress } from "./slices/loader"
import { Store } from "./store"

function checkStatus(result: Types.APIResponse<any>): void {
    if (
        Array.isArray(result.payload) &&
        result.payload.length &&
        !["OK", "NOT_FOUND"].includes(result.payload[0].status)
    )
        throw Error(result.payload[0].status)
}

function getStatus(result: Types.APIResponse<any>): string {
    if (
        Array.isArray(result.payload) &&
        result.payload.length &&
        result.payload[0].status)
        return result.payload[0].status
    return ''
}

function cleanupStorage() {
    [Enums.__STORAGE.session,
    Enums.__STORAGE.token,
    Enums.__STORAGE.accessToken,
    Enums.__STORAGE.refreshToken,
    Enums.__STORAGE.idToken,
    Enums.__STORAGE.validUntil,
    Enums.__STORAGE.user,
    Enums.__STORAGE.userLoginInfo,
    Enums.__STORAGE.accounts,
    Enums.__STORAGE.accountsLast,
    Enums.__STORAGE.lastClick,
    Enums.__STORAGE.u_pass,
    Enums.__STORAGE.notifications,
    Enums.__STORAGE.liveCongratsShown,
    Enums.__STORAGE.fa2,
    Enums.__STORAGE.demoActivation].forEach(i => localStorage.removeItem(i))
    sessionStorage.removeItem(Enums.__STORAGE.querySSOToken)
    // THPRT-6478: reset the once-per-session attribution guard so the next sign-in
    // in the same tab captures again (e.g. a different user on a shared machine).
    sessionStorage.removeItem(AD_ATTRIBUTION_SESSION_GUARD)
    Utils.clearQueryPrefillStorage()
}

function* changeLanguageSaga(action: PayloadAction<Enums.Language>) {
    const supported = action.payload in Enums.LanguageNames
    let language: string = localStorage.language = supported ? action.payload : 'en'
    switch (language) {
        case 'zh':
            language = 'zh-Hans'
            break
        case 'pt':
            language = 'pt-BR'
            break
    }
    yield call([i18n, i18n.changeLanguage], language)
    document.title = i18n.t('common:portalTitle')
    document.documentElement.setAttribute("lang", language)
    if (language === 'ar') document.documentElement.setAttribute("dir", "rtl")
    else document.documentElement.setAttribute("dir", "ltr")
}

function* fetchLoyaltyStatus(): Generator<any, any, any> {
    const MAX_RETRIES = 3
    const TIMEOUT_MS = 10000
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            const { memberResponse, timeout } = yield race({
                memberResponse: call([venusApi, venusApi.getMember]),
                timeout: delay(TIMEOUT_MS)
            })
            if (memberResponse) {
                yield put(setLoyaltyStatus(memberResponse.status))
                return
            }
            if (timeout) {
                console.warn(`Loyalty status request timed out (attempt ${attempt}/${MAX_RETRIES})`)
            }
        } catch (e: any) {
            // 4xx errors are not transient — don't retry (e.g. 404 Member not found)
            if (e?.status >= 400 && e?.status < 500) {
                return
            }
            console.error(`Failed to fetch loyalty status (attempt ${attempt}/${MAX_RETRIES})`, e)
        }
        if (attempt < MAX_RETRIES) {
            yield delay(attempt * 1_000)
        }
    }
    console.error(`Loyalty status unavailable after ${MAX_RETRIES} attempts`)
}

function* preloadApp(transaction?: any): Generator<any, any, any> {
    const preloadAppSpan = transaction ? transaction.startSpan('preload App', 'app') : undefined
    const preloadAppRequestsSpan = transaction ? transaction.startSpan('Fetch user info, accounts, app info etc.', 'external.http') : undefined
    const response = yield call(Api.multilpeTFBO,
        [
            Api.getUserPayload(),
            Api.appInfoPayload(),
            Api.getIBApplicationPayload(),
            Api.checkApplicationStatusesPayload(),
            Api.getTransactionsPayload(),
            Api.getUserAppsPayload(),
            Api.isUserVerifiedPayload(Utils.loadEncryptedString(Enums.__STORAGE.user)!),
        ], Authorize.Yes
    )
    preloadAppRequestsSpan && preloadAppRequestsSpan.addLabels({ requestID: response.id })
    preloadAppRequestsSpan && preloadAppRequestsSpan?.end()
    checkStatus(response)
    const [user,
        appInfo,
        ibAppInfo,
        appStatuses,
        transactions,
        userApps,
        isUserVerified] = response.payload
    if (['VALIDATION_ERROR', 'SYS_ERR'].includes(appInfo.status)) {
        throw Utils.statusError(appInfo)
    }
    const profile = user.result as UserProfile
    yield spawn(fetchLoyaltyStatus)
    yield put(userLoaded(profile))
    if (sessionStorage.getItem('tm_registration_pending')) {
        sessionStorage.removeItem('tm_registration_pending')
        window.dataLayer?.push({ event: 'registration_completed', user_id: profile.id })
    }
    // THPRT-6478: capture ad attribution for the signed-in user, once per session.
    // Runs on every authenticated bootstrap (login or session restore), so it
    // records returning clients who registered before capture existed; the backend
    // is first-write-wins, so it no-ops for anyone already captured at registration.
    captureAdAttribution({ oncePerSession: true })
    yield put(setUserCountry(profile.country))
    const emailVerified = isUserVerified.result
    yield put(userEmailVerified(emailVerified))
    if (ibAppInfo.status !== "NOT_FOUND" && ibAppInfo.status !== "SYS_ERR") {
        yield put(setIBApplication(ibAppInfo.result))
    } else yield put(setIBApplication(null))

    const statusesResult = (appStatuses.result || []) as Types.ApplicationStatusResponse[]
    // THPRT-6348: when a client has more than one application, prefer a fully
    // approved application over merely the newest by date. Taking the last item
    // masked an approved profile behind a newer, lesser one (e.g. LEVEL1_APPROVED),
    // which showed the client as unverified after login. Fall back to the newest
    // application when none is approved.
    const statusResult = Array.isArray(statusesResult)
        ? (statusesResult.find(s => s.application_status === 'APPROVED')
            ?? statusesResult[statusesResult.length - 1]
            ?? null)
        : null
    if (statusResult) {
        yield put(setAppStatus(statusResult.application_status as ApplicationStatus))
        yield put(setAppropriatenessLevel(statusResult.appropriateness_level))
        yield put(setApplicationApproval(statusResult.application_approval))
    }
    const apps = userApps.result as UserApplication[]
    if (apps.length > 0) {
        if (appInfo.status !== 'NOT_FOUND') yield put(setUserAppInfo(appInfo.result))
        yield put(userAppsLoaded(apps))
        const app = apps[0]
        // THPRT-6269: keyed on the parked status alone — the recorded appropriateness
        // level is not reliable (BE overwrites FAIL with PASS on level-less submits)
        if (app.country?.code2 === "AU" && app.applicationStatus === "PENDING_APPROPRIATENESS_TEST") {
            try {
                const response = yield call(Api.getApplicationCoolDownCheck, app.id)
                yield put(setApplicationCooldownCheck(response.payload[0].result))
            }
            catch (error: any) {
                console.error(error)
                apm.captureError(error)
            }
        }

        const introducedBy = apps.find(a => a.introducedBy !== undefined)?.introducedBy
        if (introducedBy) try {
            const response = yield call(Api.isThinkInvestOptionDisabled, introducedBy, profile.id)
            yield put(setAllowThinkInvest(!response.payload[0].result))
        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
    }
    yield put(getOrganizationSettings())

    const query = queryString.parse(window.location.search)
    const parsedLang = (query.lang as string) || localStorage.getItem('language')
    const language = profile.preferredLanguage
        ? profile.preferredLanguage.language_code
        : 'en'
    yield put(changeLanguage(parsedLang || language))

    //accounts cache
    /*
    if (localStorage[Enums.__STORAGE.accounts])
        try {
            const accounts = JSON.parse(localStorage[Enums.__STORAGE.accounts])
            yield put(setAccounts(accounts))
        }
        catch { yield put(getAccounts()) }
    else yield put(getAccounts())
    */
    yield put(setLoggedIn(true))
    yield put(getAccounts())
    const deposites: Transaction[] = Array.isArray(transactions.result)
        ? transactions.result.filter((x: Transaction) => (
            x.type === 'DEPOSIT'
        )) : []
    const last = deposites[deposites.length - 1]
    yield put(setLastDeposit(last ? last : null))
    yield put(Actions.checkSubscriptionStatus())
    yield put(Actions.checkUserReferrerStatus(profile.id))
    const shouldCheckTermsAndConditions = Utils.getStorageBoolean(Enums.__STORAGE.tAc)
    if (shouldCheckTermsAndConditions) {
        yield put(Actions.checkUserTermsAndConditions())
    }
    const wtr = yield select((state: Store) => state.App.redirectedFromWTR)
    const keepLogged = Utils.getStorageBoolean('keepLogged')
    if (!keepLogged && !wtr) {
        yield put(Actions.startCheckActivity())
    }

    preloadAppSpan && preloadAppSpan.end()
    yield put(bootCompleted())
}

function* reloadAppInfoSaga(): Generator<any, any, any> {
    const info = yield call(Api.appInfo)
    const status = getStatus(info)
    if (status !== 'NOT_FOUND')
        yield put(setUserAppInfo(info.payload[0].result))
}

function* afterLogin(): Generator<any, any, any> {
    yield put(Actions.getMigrationStatus())
    yield put(Actions.checkSubscriptionStatus())
    yield put(Actions.checkUserTermsAndConditions())
    yield put(Actions.checkDemoExpiration())
}

function* flushIntroducedBy(): Generator<any, any, any> {
    const ib = localStorage.getItem(__STORAGE.introducedBy)
    if (!ib) return
    // Capital Index only applies to the AU organisation, so a non-AU client must not
    // be attributed to a Capital Index IB. The onboarding marker is recorded on the
    // pure pid at mount (before the org is known); by the time this runs the org is
    // resolved, so drop the marker without persisting when it is a Capital Index pid
    // on a non-AU organisation.
    if (Utils.isCapitalIndexPid(ib)) {
        const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)
        const onboardingDomain = yield select((state: Store) => state.App.onboardingDomain)
        const portalAccountDomain = yield select((state: Store) => state.App.portalAccountDomain)
        const onboardingCountry = yield select((state: Store) => state.App.onboardingCountry)
        const domain = userProfile?.organization?.name || onboardingDomain || portalAccountDomain || onboardingCountry?.organization?.name
        if (domain !== Enums.PortalAccountDomain.AU) {
            localStorage.removeItem(__STORAGE.introducedBy)
            return
        }
    }
    try {
        yield call(Api.addAttributeToUserProfile, { introducedBy: ib })
        localStorage.removeItem(__STORAGE.introducedBy)
    } catch (e: any) {
        apm.captureError(`introducedBy attribution failed: ${e?.message}`)
    }
}

// login_submitted fires on a valid login submit (status: clicked) and again
// here at the resolved result (status: success/failed). Kept defensive so a
// tracking push can never break login. Base params are read from redux the same
// way the other events source them; ref/page_path come from the document.
// user_id and account_id are added only on success, once the profile and live
// account are loaded (source expressions match logout_clicked and the error event).
function* pushLoginResult(status: SubmittedStatus, code: SubmittedCode, email?: string | null): Generator<any, any, any> {
    try {
        const loggedIn: boolean = yield select((s: Store) => s.App.loggedIn)
        const language: string = yield select((s: Store) => s.App.language)
        const profileCountry: string | undefined = yield select((s: Store) => s.App.userProfile?.country?.code2)
        const ipCountry: string | undefined = yield select((s: Store) => s.App.userIP?.country_code)
        const profileEmail: string | undefined = yield select((s: Store) => s.App.userProfile?.email)
        let userId: string | number | null = null
        let accountId: string | number | null = null
        if (status === 'success') {
            userId = yield select((s: Store) => s.App.userProfile?.id ?? null)
            accountId = yield select((s: Store) => s.App.liveAccounts?.[0]?.account?.accountNumber ?? null)
        }
        window.dataLayer?.push(buildSigninEvent('submitted', {
            referrer: document.referrer,
            loggedIn,
            language,
            country: profileCountry || ipCountry || '',
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
            provider: 'portal',
            status,
            code,
            userId,
            accountId,
            email: email ?? profileEmail,
        }))
    } catch (e) {
        console.error(e)
    }
}

function* loginSaga({ payload }: { payload: Types.LoginActionParams }): any {
    yield put(setAutoLogout(false))
    const transaction = apm.startTransaction('Login', 'custom', { managed: true })
    const loginSpan = transaction ? transaction.startSpan('Login saga', 'app', { blocking: true } as any) : undefined
    transaction && transaction.addLabels({ email: payload.email })
    yield put(ApiSlices.login.actions.start())
    let credentialsAccepted = false
    let failureCode: SubmittedCode = 'na'
    try {
        let fa2challenge = false
        const login: Types.AuthResponse & Types.ErrorAuthResponse = yield call(Api.Login, payload)
        if (login.code === "ASE-001") {
            failureCode = login.code
            yield put(ApiSlices.login.actions.error({
                error: "NOT_AUTHORIZED",
                response: login
            }))
            throw new Error("NOT_AUTHORIZED")
        }
        yield call(Api.setAuthTokens, login.tokens)
        const status = login.status as Types.LoginResponseStatus

        switch (status) {
            case 'OK':
            case 'PENDING_APPROVAL':
            case 'PENDING_REVIEW':
            case 'PENDING_ID_ADDRESS':
            case 'PENDING_ID':
            case 'PENDING_ADDRESS':
                break
            case 'TFA_REQUIRED':
                fa2challenge = true
                break
            default:
                ///LOCKED_OUT ???
                failureCode = status
                yield put(ApiSlices.login.actions.error({
                    error: status,
                    response: login
                }))
                throw new Error(status)
        }
        // Auth-adapter accepted the credentials. Any NOT_AUTHORIZED thrown
        // beyond this point comes from downstream (TFBO can't identify the
        // user — missing userUniqueId claim, broken migration, etc.) and
        // must surface to the user instead of being hidden.
        credentialsAccepted = true

        Utils.saveEncryptedString(Enums.__STORAGE.user, payload.email.toLowerCase())
        Utils.setStorageBoolean(!!payload.keepLogged, 'keepLogged')
        Utils.saveEncryptedString(Enums.__STORAGE.u_pass, payload.password)

        yield put(setTwoFa({
            checked: true,
            enabled: fa2challenge,
            valid: !fa2challenge
        }))

        // If 2FA is required, skip preloadApp and just dispatch success
        // The Login component will handle navigation to Check2FA page
        if (fa2challenge) {
            yield put(ApiSlices.login.actions.success({ requires2FA: true }))
            yield* pushLoginResult('success', 'na', payload.email)
            return
        }

        yield call(flushIntroducedBy)
        yield call(preloadApp, transaction)
        yield call(afterLogin)
        yield put(setShowFA2Warning(!fa2challenge))

        const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)

        window.dataLayer?.push({
            'event': 'User Login',
            'user id': userProfile?.id,
        })
        window.dataLayer?.push({
            'event': 'login',
            'user_id': userProfile?.id,
        })

        yield put(setQuestions([]))

        yield put(ApiSlices.login.actions.success({}))
        yield* pushLoginResult('success', 'na', payload.email)
        // yield put(Actions.startKeepSession())
        // yield put(Actions.startRefreshAccounts())
    } catch (e: any) {
        const isUnauthorized = e instanceof Error && e.message === 'NOT_AUTHORIZED'
        // Hide only the wrong-password case (Api.Login returned ASE-001).
        // The Login form shows that inline — a popup would duplicate it.
        // Once credentials were accepted, never hide.
        const hideError = isUnauthorized && !credentialsAccepted
        if (hideError) {
            apm.captureError(`Login: ${e.message}`)
        } else if (isUnauthorized && credentialsAccepted) {
            yield put(appError(Utils.customError(
                i18n.t('common:appError.accountNotAccessible.text'),
                i18n.t('common:appError.accountNotAccessible.caption')
            )))
        } else if (e.type !== undefined) {
            yield put(appError(e))
        } else {
            yield put(appError(Utils.customError(e.message)))
        }
        const resolvedCode = failureCode !== 'na' && failureCode != null && failureCode !== ''
            ? failureCode
            : (e && (e.status || e.code)) || (e instanceof Error ? e.message : '')
        yield* pushLoginResult('failed', resolvedCode, payload.email)
        cleanupStorage()
    } finally {
        loginSpan?.end()
        transaction?.end()
    }
}


function* logoutSaga() {
    try {
        yield put(setLoggedIn(false))
        // Api.Logout()
    }
    catch (error: any) {
        console.error(error)
        apm.captureError(error)
        Sentry.captureException(error)
    }
    finally {
        yield put(Actions.stopRefreshAccounts())
        yield put(Actions.stopPullAppStatus())
        yield put(Actions.stopCheckActivity())
        yield put(ApiSlices.login.actions.clear())
        yield put(ApiSlices.logout.actions.success(true))
        cleanupStorage()
        yield call(Api.setTFBOAuth, '', '')
        yield call(Api.setAuthTokens, undefined)
        yield put(cleanupAppState())
        yield put(cleanupIBState())
        yield put(setUserInsidePortal(false))
    }
}

function* getUserSaga(): Generator<any, any, any> {
    try {
        const user = yield call(Api.getUser)
        checkStatus(user)
        yield put(userLoaded(user.payload[0].result))
    } catch (error: any) {
        console.error(error)
        apm.captureError(error)
        Sentry.captureException(error)
    }
}


function* getMigrationStatusSaga() {
    while (true)
        try {
            yield take(Actions.getMigrationStatus.type)
            const migrationStatus: Types.APIResponse<Types.MigrationInfo> = yield call(Api.getMigrationStatus)
            checkStatus(migrationStatus)
            yield put(setMigration(migrationStatus.payload[0].result))
        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
}

function* checkUserTermsAndConditionsSaga(): Generator<any, any, any> {
    while (true)
        try {
            const [, second] = yield all([
                take(Actions.checkUserTermsAndConditions.type),
                take(setAccounts.type)
            ])
            const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)
            const rawAccounts: Types.AccountStat[] = second.payload

            const hasDemoAccounts = rawAccounts.filter(a => a.account.type.toUpperCase() === 'DEMO').length > 0
            const hasLiveAccounts = rawAccounts.filter(a => a.account.type.toUpperCase() === 'LIVE').length > 0
            const isDemo = hasDemoAccounts && !hasLiveAccounts

            if (!isDemo && userProfile?.country?.organization?.name === Enums.PortalAccountDomain.TMJP) {
                const response = yield call(Api.multilpeTFBO,
                    [
                        Api.checkUserTermsAndConditionsPayload(),
                        Api.getLatestTermsAndConditionsPayload(),
                    ], Authorize.Yes
                )
                checkStatus(response)
                const [currentVersionResponse, latestTermsAndConditions] = response.payload as [Types.APIResponsePayload<Types.UserTermsAndConditions>, Types.APIResponsePayload<Types.LatestTermsAndConditions>]
                const isLoggedIn: boolean = yield select((state: Store) => state.App.loggedIn)
                if (currentVersionResponse.result.customerVersion !== latestTermsAndConditions.result.latestVersion && isLoggedIn) {
                    Utils.setStorageBoolean(true, Enums.__STORAGE.tAc)
                    yield put(setNextTermsAndConditions(latestTermsAndConditions.result))
                }
            }

        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
}

function* checkDemoExpirationSaga(): Generator<any, any, any> {
    while (true)
        try {
            const [, accountsResponse] = yield all([
                take(Actions.checkDemoExpiration.type),
                take(setAccounts.type),
            ])

            const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)
            const userApps: Types.UserApplication[] = yield select((state: Store) => state.App.userApps)
            const rawAccounts: Types.AccountStat[] = accountsResponse.payload

            if (Utils.checkIfJapanDemoExpired(rawAccounts, userProfile, userApps)) {
                yield put(setIsDemoExpired(true))
            }

        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
}

function* checkSubscriptionStatusSaga(): Generator<any, any, any> {
    while (true)
        try {
            const [, accountsResponse] = yield all([
                take(Actions.checkSubscriptionStatus.type),
                take(setAccounts.type),
            ])
            const rawAccounts: Types.AccountStat[] = accountsResponse.payload
            yield call(logoutTerminatedUsersSaga, { payload: rawAccounts, type: 'logoutTerminatedUserIfNeeded' })
        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
}

function* checkUserReferrerStatusSaga(): Generator<any, any, any> {
    while (true)
        try {
            const [userIDpayload] = yield all([
                take(Actions.checkUserReferrerStatus.type),
            ])
            const userID: number = userIDpayload.payload
            const response: Types.APIResponse<Types.UserReferrerStatus> = yield call(Api.checkUserReferrerStatus, userID)
            checkStatus(response)
            yield put(setUserReferrerStatus(response.payload[0].result))
        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
}

function* logoutTerminatedUsersSaga({ payload: accounts }: PayloadAction<Types.AccountStat[]>): Generator<any, any, any> {
    try {
        const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)
        const liveTTAccount = accounts.find(a => a.account.type.toUpperCase() === 'LIVE' && a.platformAccountType === 'ThinkTrader')

        if (userProfile?.country?.organization?.name === Enums.PortalAccountDomain.TMJP && liveTTAccount) {
            const response: Types.APIResponse<Types.GetSubscriptionStatusResponse> = yield call(Api.getSubscriptionStatus, { provider: 'BjpSub', accountNumber: liveTTAccount.account.accountNumber })
            const status = response.payload[0] ? response.payload[0].result.status : 'noRecord'
            yield put(setSubscriptionStatus(status))
            if (status === 'TERMINATED' || status === 'CANCELED') {
                yield put(Actions.logout())
                yield put(setIsSubscriptionTerminated(true))
            }
        }

    } catch (error: any) {
        console.error(error)
        apm.captureError(error)
        Sentry.captureException(error)
    }
}

function* getUserAppsSaga(): Generator<any, any, any> {
    try {
        const userApps = yield call(Api.getUserApps)
        checkStatus(userApps)
        yield put(userAppsLoaded(userApps.payload[0].result))
    } catch (e) {
        console.error(e)
    }
}

function* getOrganizationSettingsSaga(action: PayloadAction<{ country_id: number } | undefined>): Generator<any, any, any> {
    try {
        const response = yield call(Api.getOrganizationSettings, action.payload?.country_id)
        checkStatus(response)
        yield put(organizationSettingsLoaded({ settings: response.payload[0].result }))
    } catch (e) {
        console.error(e)
    }
}

function* pullAppStatusWorker(): Generator<any, any, any> {
    let counter = 1
    const interval = appConfig.PULL_APP_STATUS_SEC || 30
    while (true) {
        if (counter > 1) {
            yield delay(interval * 1000)
        }
        try {
            counter++
            const apps: Types.UserApplication[] = yield select((state: Store) => state.App.userApps)
            const latestApp = apps.slice().sort((x, y) => Utils.sortDates('descend', x.createdDate, y.createdDate))[0]
            const response = yield call(Api.multilpeTFBO,
                [
                    Api.getUserAppsPayload(),
                    Api.checkApplicationStatusByIDPayload(latestApp.id),
                ], Authorize.Yes
            )
            checkStatus(response)
            const [userApps, appStatusResponse] = response.payload as [Types.APIResponsePayload<any>, Types.APIResponsePayload<Types.ApplicationStatusResponse>]
            const status = appStatusResponse.result.application_status as ApplicationStatus
            const applicationApproval = appStatusResponse.result.application_approval
            yield put(setAppStatus(status))
            yield put(setApplicationApproval(applicationApproval))
            yield put(userAppsLoaded(userApps.result))
            if (status === 'APPROVED') {
                yield put(Actions.getAccounts())
            }
        } catch (e) {
            console.error(e)
        }
    }
}

function* pullAppStatusSaga() {
    while (true) {
        try {
            yield take(Actions.startPullAppStatus.type)
            yield race([
                call(pullAppStatusWorker),
                take(Actions.stopPullAppStatus.type)
            ])
        } catch (error: any) {
            console.error(error)
            apm.captureError(error)
        }
    }
}

function* fa2checkedSaga(): Generator<any, any, any> {
    try {
        const transaction = apm.startTransaction('FA2 checked', 'custom', { managed: true })
        yield call(flushIntroducedBy)
        yield call(preloadApp, transaction)
        yield put(setTwoFa({ checked: true, enabled: true, valid: true }))
        yield call(afterLogin)
        transaction?.end()
    } catch (error) {
        console.error(error)
    }
}

function* appFailedSaga() {
    yield put(setUserAppInfo({}))
    yield put(Actions.getUserApps())
}

function* corporateAppSubmitedSaga(action: PayloadAction<AppSubmitedParams>) {
    yield put(Actions.getUser())
    yield put(setNewCreatedAccount(action.payload.account))
    yield* getAccountsSaga()
}

function* appSubmitedSaga(action: PayloadAction<AppSubmitedParams>) {
    yield put(Actions.getUser())
    const transaction = apm.startTransaction('Submit App', 'custom', { managed: true })
    const submitSagaSpan = transaction ? transaction.startSpan('Submit App', 'app', { blocking: true } as any) : undefined
    const skipUploadDocs = (status: ApplicationStatus) => {
        return ['APPROVED', 'PENDING_REVIEW'].includes(status)
    }
    const { appStatus: initialStatus, countryId, account, applicationId } = action.payload

    if (countryId === undefined || countryId === null) {
        yield put(appError(Utils.customError(i18n.t('common:appError.unableToLoadProfile'))))
        Sentry.captureMessage('appSubmitedSaga: countryId missing in action payload', 'warning')
        submitSagaSpan && submitSagaSpan.end()
        transaction && transaction.end()
        return
    }

    yield put(setNewCreatedAccount(account))
    transaction && transaction.addLabels({ initialStatus })

    function* checkAppStatus(): Generator<any, any, any> {
        try {
            const getAppStatusSpan = transaction?.startSpan('getAppStatus', 'external.http')
            const result: Types.APIResponse<Types.ApplicationStatusResponse> = yield call(Api.checkApplicationStatusByID, applicationId)
            getAppStatusSpan?.end()
            checkStatus(result)
            return result.payload[0].result.application_status
        } catch {
            yield put(setAppStatus(initialStatus))
            return initialStatus
        }
    }

    function* setStatus(status: ApplicationStatus) {
        yield put(Actions.getUserApps())
        yield* getAccountsSaga()
        yield put(setAppStatus(status))
    }

    try {
        const apps: Types.UserApplication[] = yield select((state: Store) => state.App.userApps)
        const hasApproved = apps.find(a => a.applicationStatus === 'APPROVED')
        if (hasApproved) {
            yield* setStatus('APPROVED')
            window.navigateToPage && window.navigateToPage(Routes.dashboard.accounts.root)
        }
        else {
            const getKYCProviderSpan = transaction?.startSpan('getKYCProvider', 'external.http')
            const result: APIResponse<KYCProvider> = yield call(Api.getKYCProvider, countryId)
            getKYCProviderSpan?.end()
            checkStatus(result)

            const provider = result.payload[0].result
            transaction && transaction.addLabels({ provider })
            yield put(setKYCProvider(provider))
            let skip = provider.toUpperCase() !== 'THISISME' ||
                skipUploadDocs(initialStatus) || hasApproved


            if (!skip) {
                const steps = appConfig.VERIFICATION_ATTEMPTS || 3
                const interval = appConfig.VERIFICATION_INTERVAL_SEC || 3
                let step = 1
                yield put(setProgressSteps(steps + 1))
                yield put(setProgressStep(step))
                yield put(setShowProgress(true))
                for (let i = 0; i < steps; i++) {
                    const status = yield* checkAppStatus()
                    if (skipUploadDocs(status)) {
                        yield* setStatus(status)
                        skip = true
                        break
                    } else {
                        yield put(setProgressStep(++step))
                        if (i < steps - 1) {
                            const delaySpan = transaction?.startSpan('delay', 'app')
                            yield delay(interval * 1000)
                            delaySpan?.end()
                        }
                    }
                }
                yield put(setShowProgress(false))
                if (!skip) yield* setStatus(initialStatus)
            }
            else yield* setStatus(initialStatus)
        }
        if (initialStatus === 'APPROVED') yield put(Actions.getUserApps())
    }
    catch (error: any) {
        console.error(error)
        apm.captureError(error)
        Sentry.captureException(error)
        yield* setStatus(initialStatus)
    }
    submitSagaSpan && submitSagaSpan.end()
    transaction && transaction.end()
}

function* CheckActivitySaga() {

    function* checkActivityWorker() {
        while (true) {
            try {
                yield delay(30 * 1000)
                const timeout: number = yield select((state: Store) => state.App.inactivityTimeout)
                if (Utils.nowGreaterThen(Enums.__STORAGE.lastClick, timeout, 'minutes')) {
                    yield put(Actions.logout())
                    yield put(setAutoLogout(true))
                    setTimeout(() =>
                        window.navigateToPage && window.navigateToPage(Routes.account.login), 1000)
                }

            }
            catch (error: any) {
                console.error(error)
                apm.captureError(error)
            }
        }
    }

    while (true) {
        yield take(Actions.startCheckActivity.type)
        yield race([
            call(checkActivityWorker),
            take(Actions.stopCheckActivity.type)
        ])
    }
}

/*
function* keepRefreshingAccounts() {
    function* keepRefreshingAccountsWorker(interval: number): Generator<any, any, any> {
        while (true) {
            try {
                yield delay(interval)
                const applications: Types.UserApplication[] | null = yield select((state: Store) => state.App.userApps)
                if (Utils.filterOpenApps(applications)?.length) {
                    yield put(Actions.getUserApps())
                }
                const response = yield call(Api.getAccountStat)
                checkStatus(response)
                yield put(setAccounts(response.payload.result.data))
            } catch { }
        }
    }

    while (true) {
        yield take(Actions.startRefreshAccounts.type)
        yield race([
            call(keepRefreshingAccountsWorker, 60000),
            take(Actions.stopRefreshAccounts.type)
        ])
    }
}
*/
function* getQuestionsSaga(action: PayloadAction<{ id: number } | undefined>): Generator<any, any, any> {
    try {
        let id = action.payload?.id || 0
        if (!id) {
            const userProfile: Types.UserProfile | null = yield select((state: Store) => state.App.userProfile)
            if (userProfile) id = userProfile.organization.id
            else {
                //not logged in yet
                const orgs: Types.Organization[] = yield select((state: Store) => state.App.organizations)
                const countryId = yield select((state: Store) => state.App.onboardingCountry?.organization?.id)
                const onboardingDomain = yield select((state: Store) => state.App.onboardingDomain)
                const onboardingId = orgs.find(o => o.name === onboardingDomain)?.id
                id = onboardingId || countryId
            }
        }
        const response = yield call(Api.getQuestions, id)
        yield put(setQuestions(response.payload[0].result))
    } catch { }
}

function apiCall(slice: any, apiCall: any) {
    return function* (action: any): Generator<any, any, any> {
        let count = 3, success = false
        yield put(slice.actions.start(action.payload))
        while (count && !success)
            try {
                const result = yield call(apiCall, action.payload)
                checkStatus(result)
                yield put(slice.actions.success({ result: result, request: action.payload }))
                success = true
            } catch (e) {
                count--
                if (count === 0)
                    yield put(slice.actions.error({ error: e, request: action.payload }))
                else
                    yield delay(3 * 1000)
            }
    }
}

function* onStartSaga(): Generator<any, any, any> {

    function* initializationError(e: any): Generator<any, any, any> {
        console.log(e)
        Utils.postMessageToWTR({ message: 'restart' })
        yield put(cleanupAppState())
        yield put(cleanupIBState())
        cleanupStorage()
        yield put(initError(e))
        yield put(appError(Utils.customError(e.message)))

    }

    function* setLanguage(languages?: Types.PreferredLanguage[], country_code?: string): Generator<any, any, any> {
        const query = queryString.parse(window.location.search)
        const lang = query?.lang || localStorage.getItem('language')
        const queryLang = languages?.find(x => x.language_code === lang)
        if (queryLang) {
            yield put(changeLanguage(String(lang)))
            return
        }
        const defaultLang: string = localStorage.language ||
            Utils.getLanguage(country_code)
        yield put(changeLanguage(defaultLang))
    }

    function* initFromServer(): Generator<any, any, any> {
        try {
            const response = yield call(Api.multilpeTFBO,
                [
                    Api.getIPPayload(),
                    Api.getPreferredLanguagesPayload(),
                ], Authorize.No)
            checkStatus(response)
            const [ip, languages] = response.payload

            yield put(setUserIP(ip.result))
            yield put(setPreferredLanguages(languages.result))
            yield* setLanguage(languages.result, (ip.result as Types.GetIPResponse)?.country_code)
        }
        catch (e) { yield* initializationError(e) }
    }
    function* ssoLogin(parsedEmail: string, parsedToken: string, query: queryString.ParsedQuery<string>): Generator<any, any, any> {
        try {
            const ssoSuccess: boolean = yield call(Api.validateSsoToken, {
                ssoCode: parsedToken,
            })
            if (ssoSuccess) {
                Utils.saveEncryptedString(Enums.__STORAGE.user, parsedEmail)
                const fa2: Types.FA2Status = yield call(Api.get2FaStatus)
                yield put(setTwoFa({ checked: true, enabled: fa2.status === 'ENABLED', valid: true }))
                return
            }
            // ssoSuccess === false is an expected BE rejection (expired/replayed token).
            // User falls back to manual login; track volume in APM only — there's
            // no portal-side action to take, so don't pollute Sentry.
            apm.captureError('SSO token rejected by BE')
        } catch (e) {
            console.error('>> error validation sso', e)
            const error = e instanceof Error ? e : new Error('SSO validation failed')
            apm.captureError(error)
            Sentry.captureException(error)
        }
        yield put(cleanupAppState())
        yield put(cleanupIBState())
        cleanupStorage()
    }
    const transaction = apm.startTransaction('On start saga', 'custom')
    try {
        // Capture the marketing landing URL for later submission to TFBO BEFORE
        // appInit runs: appInit can redirect (locale, /onboarding resume, SSO
        // exchange) and strip the query string, so reading window.location.href
        // afterwards would lose utm_* attribution. Strip only PII/credential
        // params here; keep utm_* and other attribution context.
        const buildUtmLink = (): URL => {
            const url = new URL(window.location.href)
            ;[...Enums.QUERY_PREFILL_FIELDS, 'ssoToken', 'pwrt', 'keepmesignedin'].forEach(p => url.searchParams.delete(p))
            return url
        }
        // A tab keeps its sessionStorage across navigations, so the FIRST portal
        // URL it sees would otherwise win permanently. When that first hop carries
        // no utm (direct visit, SSO handoff, an interstitial redirect) it used to
        // shadow a later utm-tagged landing in the same tab, dropping attribution.
        // Re-capture to UPGRADE a stored URL with no attribution, but never downgrade a good
        // one with a later navigation that has none (first-attribution-touch wins). A Google
        // click id (gclid or dclid) counts as attribution, since auto-tagged ad clicks carry no utm_.
        const hasUtmParams = (search: string): boolean => /(?:^|[?&])(?:utm_|gclid=|dclid=)/i.test(search)
        const storedUtmLink = sessionStorage.getItem(Enums.__STORAGE.utmLink)
        const storedSearch = (() => {
            if (!storedUtmLink) return ''
            try { return new URL(storedUtmLink).search }
            catch {
                Sentry.addBreadcrumb({ category: 'utmLink', level: 'warning', message: 'stored utmLink is not a valid URL' })
                return ''
            }
        })()
        const incomingUtmLink = buildUtmLink()
        if (!storedUtmLink || (!hasUtmParams(storedSearch) && hasUtmParams(incomingUtmLink.search))) {
            sessionStorage.setItem(Enums.__STORAGE.utmLink, incomingUtmLink.toString())
        }

        // THPRT-6415: Stargate affiliate attribution capture (additive; backend and
        // TTL handling live in src/utils/stargateAttribution.ts, switchable via
        // SG_STORAGE_MODE). Runs BEFORE appInit, like the utmLink capture above,
        // because appInit can redirect and strip the query.
        captureStargateAttribution()

        yield call(() => appInit)
        // When user is coming from WTR with ssoToken in GET params (SSO login)
        const query = queryString.parse(window.location.search)
        // queryString.parse returns string | string[] | null. A duplicate key
        // like ?X=a&X=b yields a string[]; the previous `as string` casts lied
        // to TypeScript and the downstream String() call would join values
        // with a comma, corrupting tokens, locales, and attribution IDs.
        const coerceQueryParam = (raw: unknown, label: string, category = 'boot-query'): string | null => {
            if (Array.isArray(raw)) {
                Sentry.addBreadcrumb({
                    category,
                    level: 'warning',
                    message: `duplicate ${label} on landing URL; using first value`,
                    data: { count: raw.length }
                })
                const first = raw[0]
                return typeof first === 'string' && first ? first : null
            }
            return typeof raw === 'string' && raw ? raw : null
        }
        const sanitizeQueryParam = (raw: unknown, label: string, maxLength: number): string | null => {
            const value = coerceQueryParam(raw, label)
            if (!value) return null
            const trimmed = value.trim().slice(0, maxLength)
            return trimmed.length > 0 ? trimmed : null
        }
        const capturedPrefill = Enums.QUERY_PREFILL_FIELDS.reduce(
            (acc, f) => ({ ...acc, [f]: sanitizeQueryParam(query[f], f, Enums.QUERY_PREFILL_MAX_LENGTH[f]) }),
            {} as Record<Enums.QueryPrefillField, string | null>
        )
        const parsedEmail = capturedPrefill.email || sessionStorage.getItem(Enums.__STORAGE.queryEmail)
        const parsedSSOToken = coerceQueryParam(query.ssoToken, 'ssoToken') || sessionStorage.getItem(Enums.__STORAGE.querySSOToken)
        const parsedReferrer = coerceQueryParam(query.referrer, 'referrer')
        const parsedWtrApp = coerceQueryParam(query.wtrApp, 'wtrApp')
        const parsedSource = coerceQueryParam(query.source, 'source')
        const parsedKeepmesignedin = coerceQueryParam(query.keepmesignedin, 'keepmesignedin') as 'yes' | 'no' | null
        const pageView = localStorage.getItem('pageView')
        const referrer = queryString.parse(document.referrer)
        const aid = coerceQueryParam(query.aid, 'aid')
            || coerceQueryParam(referrer.aid, 'referrer.aid')
            || sessionStorage.getItem(Enums.__STORAGE.queryAid)
        if (aid) {
            sessionStorage.setItem(Enums.__STORAGE.queryAid, aid)
            yield put(setIB({ afsAid: aid }))
        }
        const debug = coerceQueryParam(query.debug, 'debug')
        if (debug)
            yield put(debugMode(debug.toUpperCase() === "TRUE"))
        const emailVerificationStatus = coerceQueryParam(query.emailVerificationStatus, 'emailVerificationStatus')
        if (emailVerificationStatus)
            yield put(userEmailPreverified(emailVerificationStatus.toUpperCase() === "TRUE"))
        const pwrt = coerceQueryParam(query.pwrt, 'pwrt')
        if (pwrt)
            yield put(setPasswordResetToken(pwrt))
        const lang = coerceQueryParam(query.lang, 'lang')
        if (lang)
            localStorage.setItem('language', lang)
        const theme = coerceQueryParam(query.theme, 'theme')
        if (theme)
            localStorage.setItem('theme', theme.toLowerCase())
        if (parsedSource)
            sessionStorage.setItem('parsedSource', parsedSource)
        // Detect marketing-site origin. ?src (forwarded by thinkmarkets.com
        // per WD-474, also usable as a test override) takes precedence over
        // document.referrer parsing, which is the production path now that
        // marketing's referrer-policy passes the path segment.
        const srcParam = coerceQueryParam(query.src, 'src')?.toLowerCase() ?? null
        const referrerSrc = /^https?:\/\/(?:www\.)?thinkmarkets\.com\/([^\/?#]+)/i
            .exec(document.referrer)?.[1]?.toLowerCase() ?? null
        // Prefer an EU signal from either source — a non-EU ?src must not mask a
        // genuine /eu referrer — then fall back to plain ?src-over-referrer order.
        const detectedSrc = ([srcParam, referrerSrc].find(isEuOriginSrc) ?? srcParam ?? referrerSrc)
            ?.slice(0, MAX_ORIGIN_SRC_LENGTH) ?? null
        if (detectedSrc) {
            // Keep an EU origin sticky: a later non-EU hop in the same session must
            // not clear EU treatment that an earlier hop already locked in.
            const storedSrc = sessionStorage.getItem(__STORAGE.originSrc)
            if (!(isEuOriginSrc(storedSrc) && !isEuOriginSrc(detectedSrc))) {
                yield put(setOriginSrc(detectedSrc))
            }
        }
        const platform = coerceQueryParam(query.platform, 'platform')
        if (platform)
            localStorage.setItem(Enums.__STORAGE.singlePlatform, platform.toUpperCase())
        const utm_source = coerceQueryParam(query.utm_source, 'utm_source')
        if (utm_source) {
            localStorage.setItem(Enums.__STORAGE.utm_source, utm_source)
            if (utm_source.toUpperCase() === 'TRADINGVIEW')
                localStorage.setItem(Enums.__STORAGE.singlePlatform, 'THINKTRADER')
        }

        Enums.QUERY_PREFILL_FIELDS.forEach(f => {
            const value = capturedPrefill[f]
            if (value) Utils.setQueryPrefill(f, value)
        })

        const preservedParams = new URLSearchParams()
        ;(['pid', 'type', 'cid'] as const).forEach(p => {
            const value = coerceQueryParam(query[p], p, 'ib-attribution')
            if (value) preservedParams.set(p, value)
        })
        // Consumed post-boot by App.checkWTR (sso/status/transaction_id) and
        // Account/Create (country). Both effects await the same appInit promise
        // as this saga; the saga's continuation wins the microtask race, so
        // without preservation these reads silently see an empty query.
        ;(['sso', 'status', 'transaction_id', 'country'] as const).forEach(p => {
            const value = coerceQueryParam(query[p], p)
            if (value) preservedParams.set(p, value)
        })
        const preservedQuery = preservedParams.toString()
        const replacedUrl = preservedQuery
            ? `${window.location.pathname}?${preservedQuery}`
            : window.location.pathname
        window.history.replaceState({}, '', replacedUrl)
        if (parsedKeepmesignedin) {
            Utils.setStorageBoolean(parsedKeepmesignedin === 'yes', 'keepLogged')
        }

        if (parsedEmail && parsedSSOToken) {
            sessionStorage.setItem(Enums.__STORAGE.querySSOToken, parsedSSOToken)
            try {
                yield put(Actions.logout())
                yield* ssoLogin(parsedEmail, parsedSSOToken, query)
            } finally {
                // cleanupStorage (called by logoutSaga via the put(logout)
                // above and by ssoLogin itself) wipes the queryEmail prefill
                // key. Re-write it so the post-SSO-failure login form can
                // prefill the email field.
                if (capturedPrefill.email) Utils.setQueryPrefill('email', capturedPrefill.email)
                else sessionStorage.removeItem(Enums.__STORAGE.queryEmail)
                sessionStorage.removeItem(Enums.__STORAGE.querySSOToken)
            }
        }
        else yield put(setLandingPage({ path: window.location.pathname }))

        const referrerValue = parsedReferrer || sessionStorage.getItem(Enums.__STORAGE.queryReferrer)
        if (referrerValue === 'wtr') {
            sessionStorage.setItem(Enums.__STORAGE.queryReferrer, referrerValue)
            yield put(setRedirectedFromWTR(true))
        }

        if (parsedWtrApp === 'true') {
            sessionStorage.setItem('wtrApp', parsedWtrApp)
        }

        if (pageView === 'WTR') {
            yield put(setRedirectedFromWTR(true))
            localStorage.removeItem('pageView')
        }
        const restrictions = RESTRICTED_DOMAINS.find(d => d.host === window.location.hostname)
        if (restrictions) yield put(setAllowedPlatforms(restrictions.platforms))

        if (document.referrer.includes('asahimarkets') ||
            window.location.hostname.includes('asahimarkets'))
            localStorage.setItem(Enums.__STORAGE.utm_source, 'asahimarkets')

        //for testing Japan portal
        if (Utils.isTestEnv()) {
            if (query.lang === 'ja')
                localStorage.setItem(Enums.__STORAGE.utm_source, 'asahimarkets')
        }

        // One-shot cleanup of the legacy __STORAGE.init blob (former key 'i___'),
        // removed in 3.12.16 alongside the initFromStorage cache. Drop after 3.12.18.
        const LEGACY_INIT_STORAGE_KEY = 'i___'
        try { localStorage.removeItem(LEGACY_INIT_STORAGE_KEY) } catch {}
        yield* initFromServer()
        if (localStorage.getItem(Enums.__STORAGE.utm_source) === 'asahimarkets') {
            yield put(setPortalAccountDomain(Enums.PortalAccountDomain.TMJP))
            yield put(changeLanguage('ja'))
            yield call(loadCountriesSaga)
            const countries: Types.Country[] = yield select((state: Store) => state.App.countries)
            const jp = countries.find(c => c.code3 === 'JPN')
            if (jp) yield put(setUserCountry(jp))
        }
        try {
            const session = yield call(Api.VerifyTokens)
            if (!session) throw new Error('session expired')
            const fa2 = Utils.loadEncryptedObject<Types.FA2>(Enums.__STORAGE.fa2)
            if (fa2.enabled !== undefined &&
                fa2.checked !== undefined &&
                fa2.valid !== undefined)
                yield put(setTwoFa({
                    enabled: fa2.enabled,
                    checked: fa2.checked,
                    valid: fa2.valid
                }))
            else throw new Error('Invalid stored 2FA object')
            yield* preloadApp(transaction)
            // yield put(Actions.startRefreshAccounts())
        }
        catch (e) {
            yield put(cleanupAppState())
            yield put(cleanupIBState())
            cleanupStorage()
            yield put(bootCompleted())
        }
    }
    catch (e) {
        yield* initializationError(e)
        yield put(bootCompleted())
    }
    transaction?.end()
    try {
        if (!sessionStorage.getItem(USER_LANDED_SESSION_KEY)) {
            const loggedIn: boolean = yield select((s: Store) => s.App.loggedIn)
            const language: string = yield select((s: Store) => s.App.language)
            const profileCountry: string | undefined = yield select((s: Store) => s.App.userProfile?.country?.code2)
            const ipCountry: string | undefined = yield select((s: Store) => s.App.userIP?.country_code)
            const landingPath: string | undefined = yield select((s: Store) => s.App.landingPage?.path)
            const originSrc: string | null = yield select((s: Store) => s.App.originSrc)
            const payload = buildUserLandedEvent({
                pathname: landingPath || window.location.pathname,
                referrer: document.referrer,
                loggedIn,
                language,
                country: profileCountry || ipCountry || '',
                portalHost: window.location.hostname,
                marketSegment: originSrc,
            })
            if (payload) {
                window.dataLayer?.push(payload)
                sessionStorage.setItem(USER_LANDED_SESSION_KEY, '1')
            }
        }
    } catch (e) {
        console.error(e)
    }
}

function* getAccountsSaga(): Generator<any, any, any> {
    try {
        const transaction = apm.startTransaction('Get accounts', 'custom', { managed: true })
        const getAccountSpan = transaction?.startSpan('getAccount', 'external.http')
        yield put(ApiSlices.getAccounts.actions.start())
        const result = yield call(Api.getAccountStat)
        checkStatus(result)
        yield put(setAccounts(result.payload[0].result.data))
        yield put(ApiSlices.getAccounts.actions.success(true))
        //accounts cache
        // localStorage[Enums.__STORAGE.accounts] = JSON.stringify(result.payload[0].result.data)
        // Utils.writeTimestamp(Enums.__STORAGE.accountsLast)
        getAccountSpan?.end()
        transaction?.end()

    } catch (e: any) {
        yield put(ApiSlices.getAccounts.actions.error(e.message))
        yield put(appError(Utils.customError(e.message)))
    }
}

function* launchAppPreload(): Generator<any, any, any> {
    try {
        const transaction = apm.startTransaction('Simplified onboarding creation', 'custom', { managed: true })
        yield call(preloadApp, transaction)
        try {
            const fa2: Types.FA2Status = yield call(Api.get2FaStatus)
            yield put(setTwoFa({ checked: true, enabled: fa2.status === 'ENABLED', valid: true }))
        } catch {
            yield put(setTwoFa({ checked: true, enabled: false, valid: true }))
        }
    } catch (error) {
        console.error(error)
    }
}

const FETCH_RETRY_ATTEMPTS = 3
const FETCH_RETRY_DELAY_MS = 2000
const FETCH_ATTEMPT_TIMEOUT_MS = 8000

// Bounds each attempt and aborts the request on timeout, so a hung endpoint frees its
// connection at FETCH_ATTEMPT_TIMEOUT_MS instead of leaking until the browser's ~300s default.
// apiFn must accept a trailing AbortSignal (e.g. Api.getCountries, Api.getDocumentTypes).
function* fetchOk<F extends (...args: any[]) => any>(apiFn: F, ...args: Parameters<F>): Generator<any, any, any> {
    const controller = new AbortController()
    const argsWithSignal = [...args, controller.signal] as Parameters<F>
    const { response } = yield race({
        response: call(function* (): Generator<any, any, any> {
            const r = yield call(apiFn, ...argsWithSignal)
            checkStatus(r)
            return r
        }),
        timeout: delay(FETCH_ATTEMPT_TIMEOUT_MS),
    })
    if (!response) {
        controller.abort()
        throw new Error('fetchOk attempt timed out')
    }
    return response
}

function* loadDocTypesSaga(): Generator<any, any, any> {
    const existing: Types.DocType[] = yield select((state: Store) => state.App.docTypes)
    if (existing.length > 0) return
    try {
        const response = yield retry(FETCH_RETRY_ATTEMPTS, FETCH_RETRY_DELAY_MS, fetchOk, Api.getDocumentTypes)
        yield put(docTypesLoaded(response.payload[0].result))
    }
    catch (e: any) {
        console.error('loadDocTypes failed', e)
        apm.captureError(e)
        Sentry.captureException(e)
        yield put(appError(Utils.customError(i18n.t('common:appError.unableToLoadDocTypes'))))
    }
}

function* loadCountriesSaga(): Generator<any, any, any> {
    const existing: Types.Country[] = yield select((state: Store) => state.App.countries)
    if (existing.length > 0) return
    try {
        const response = yield retry(FETCH_RETRY_ATTEMPTS, FETCH_RETRY_DELAY_MS, fetchOk, Api.getCountries, true)
        const countries = response.payload[0].result as Types.Country[]
        const ip: Types.GetIPResponse | null = yield select((state: Store) => state.App.userIP)
        yield put(initCountries({ ip: ip || undefined, countries }))
    }
    catch (e: any) {
        console.error('loadCountries failed', e)
        apm.captureError(e)
        Sentry.captureException(e)
    }
}

function* watchAsync() {
    yield takeEvery(changeLanguage, changeLanguageSaga)
    yield takeLeading(Actions.loadDocTypes, loadDocTypesSaga)
    yield takeLeading(Actions.loadCountries, loadCountriesSaga)
    yield takeLeading(Actions.login, loginSaga)
    yield takeLeading(Actions.logout, logoutSaga)
    yield takeLeading(Actions.getUser, getUserSaga)
    yield takeLeading(Actions.getUserApps, getUserAppsSaga)
    yield takeLeading(Actions.reloadAppInfo, reloadAppInfoSaga)
    yield takeEvery(Actions.getOrganizationSettings, getOrganizationSettingsSaga)
    yield takeLeading(Actions.appSubmited, appSubmitedSaga)
    yield takeLeading(Actions.corporateAppSubmited, corporateAppSubmitedSaga)
    yield takeEvery(Actions.appFailed, appFailedSaga)
    yield takeLeading(Actions.getQuestions, getQuestionsSaga)
    yield takeEvery(Actions.fa2checked, fa2checkedSaga)
    yield takeLeading(Actions.getAccounts, getAccountsSaga)
    yield takeLeading(Actions.logoutTerminatedUserIfNeeded, logoutTerminatedUsersSaga)
    yield takeLeading(Actions.launchAppPreload, launchAppPreload)
    yield takeLeading(Actions.getBankAccounts,
        apiCall(ApiSlices.getBankAccounts, Api.getBankAccounts))
    yield takeLeading(Actions.getAppStatus,
        apiCall(ApiSlices.getAppStatus, Api.checkApplicationStatusByID))
    yield takeLeading(Actions.sendLastField,
        apiCall(ApiSlices.sendLastField, Api.sendLastField))
}

export default function* rootSaga() {
    yield fork(watchAsync)
    yield fork(CheckActivitySaga)
    // yield fork(keepRefreshingAccounts)
    yield fork(pullAppStatusSaga)
    yield fork(getMigrationStatusSaga)
    yield fork(checkUserTermsAndConditionsSaga)
    yield fork(checkDemoExpirationSaga)
    yield fork(checkSubscriptionStatusSaga)
    yield fork(checkUserReferrerStatusSaga)
}

export { onStartSaga }


